/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Grid } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import QueryDetailLink, {
  buildQueryDetailTarget,
  QueryDetailEditAction,
} from '../components/QueryDetailLink';
import AnalyticTable from 'components/kea/tables/AnalyticTable';
import CardContainer from 'components/kea/CardContainer';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import AnalyticsMetricBar from '../components/AnalyticsMetricBar';
import AnalyticsReportActions from '../components/AnalyticsReportActions';
import {
  loadAnalyticsReport,
  selectZeroResults,
  selectTimeframe,
  actions as analyticsActions,
} from 'slices/AnalyticsSlice';
import { rulesForQuery, useQueryRuleIndex } from '../components/useQueryRuleIndex';

/**
 * Bridge-backed Zero Results page.
 *
 * Column labels use "Searches" (not "Users") because the plugin analytics table
 * has no user identity — event counts only.
 *
 * No sidebar widgets: both legacy widgets for this page use dishonest labels.
 * - AffectedUsers: shows `users.filteredTotal` (search event count) as "Affected users"
 * - QueriesWithoutResults: shows `queries.filteredTotal` = `$totalSearches` (event count)
 *   labeled as "Queries without results" — does not count distinct queries.
 */
const QueryNoResultBridge = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const localizeMessage = useLocalizedMessage();
  const zeroResults = useSelector(selectZeroResults);
  const timeframe = useSelector(selectTimeframe);
  const { currentShop } = useSelector((store) => store.app);
  const { capabilities } = useShellAppContext();
  const queryRuleIndex = useQueryRuleIndex();
  const detailEnabled = Boolean(capabilities?.analyticsQueryDetailV1);
  const exportEnabled = Boolean(capabilities?.analyticsReportExportV1);
  const returnTo = `${location.pathname}${location.search}`;

  const { isFetching, data, error } = zeroResults;
  const maximumSearches = Math.max(1, ...data.map((row) => Number(row.searches || 0)));

  useEffect(() => {
    if (!currentShop) return;
    dispatch(loadAnalyticsReport({ shopId: currentShop, reportType: 'zero_results' }));
  }, [dispatch, currentShop, timeframe]);

  const handleTimeframeChange = (range) => {
    dispatch(analyticsActions.setTimeframe(range));
  };

  const headCells = [
    {
      id: 'query', numeric: false, label: localizeMessage('analytics.table.query'), sortKey: 'queryText',
    },
    {
      id: 'searches',
      numeric: true,
      label: localizeMessage('analytics.table.searches'),
      render: (value) => (
        <AnalyticsMetricBar
          value={value}
          maximum={maximumSearches}
          label={localizeMessage('analytics.table.searches')}
          tone="zeroResults"
          compact
        />
      ),
      compact: true,
      width: 112,
    },
    detailEnabled && {
      id: 'edit',
      numeric: false,
      label: localizeMessage('analytics.table.settings'),
      visuallyHiddenLabel: true,
      action: true,
      sortable: false,
      width: 64,
    },
  ].filter(Boolean);

  const prepareRows = (rows) => rows.map((el, i) => ({
    ...el,
    queryText: el.query,
    detailTarget: detailEnabled
      ? buildQueryDetailTarget(currentShop, el.query, returnTo)
      : '',
    query: (
      <QueryDetailLink
        shopId={currentShop}
        query={el.query}
        enabled={detailEnabled}
        activeRuleCount={rulesForQuery(queryRuleIndex, el.query).active.length}
      />
    ),
    edit: (
      <QueryDetailEditAction
        shopId={currentShop}
        query={el.query}
        enabled={detailEnabled}
      />
    ),
    id: `${el.query}-${i}`,
  }));

  const handleRowActivate = (row) => {
    if (row.detailTarget) history.push(row.detailTarget);
  };

  return (
    <SearchFeaturePageShell
      pageId="analyticQueryNoResult"
      actions={(
        <AnalyticsReportActions
          exportEnabled={exportEnabled}
          shopId={currentShop}
          reportType="zero_results"
          timeframe={timeframe}
          onSubmitRange={handleTimeframeChange}
          isFetching={isFetching}
          localizeMessage={localizeMessage}
        />
      )}
    >
      <Grid spacing={4} container>
        <Grid item xs={12}>
          <CardContainer
            headerId="search.analytics.queryNoResult.header"
            subheaderId="search.analytics.queryNoResult.subheader"
            styleName="noMargin"
            tableSurface
            isFetching={isFetching}
            error={error}
          >
            <AnalyticTable
              rows={prepareRows(data)}
              headCells={headCells}
              orderById="searches"
              fixedLayout
              interactiveRows={detailEnabled}
              onRowActivate={handleRowActivate}
              minWidth={560}
            />
          </CardContainer>
        </Grid>
      </Grid>
    </SearchFeaturePageShell>
  );
};

export default QueryNoResultBridge;
